import { authApiService, publicApiService, AUTH_ENDPOINTS, PUBLIC_ENDPOINTS, refreshAccessToken, getNormalizedApiBaseUrl } from '../config/api';
import { generateGUID } from '../utils/guid';

/**
 * Supported content types and their corresponding file extensions
 * Supports Images, Documents, CAD/BIM/3D Models, and Structural/Collaboration files
 */
export const SUPPORTED_CONTENT_TYPES = {
  // Images
  'image/jpeg': '.jpg',
  'image/jpg': '.jpg',
  'image/png': '.png',
  'image/webp': '.webp',
  'image/gif': '.gif',
  'image/tiff': '.tif',
  'image/bmp': '.bmp',
  'image/heif': '.heif',
  'image/heic': '.heic',
  // Documents
  'application/pdf': '.pdf',
  'application/msword': '.doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
  'application/vnd.ms-excel': '.xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'application/vnd.ms-powerpoint': '.ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
  // CAD / BIM / 3D Models - Multiple MIME type variations for browser compatibility
  'application/vnd.dwg': '.dwg',
  'application/acad': '.dwg',
  'application/x-acad': '.dwg',
  'application/autocad_dwg': '.dwg',
  'application/dwg': '.dwg',
  'image/vnd.dwg': '.dwg',
  'application/dxf': '.dxf',
  'application/dwt': '.dwt',
  'application/dws': '.dws',
  'application/dwf': '.dwf',
  'application/dwfx': '.dwfx',
  'application/vnd.microstation.dgn': '.dgn',
  'application/x-rvt': '.rvt',
  'application/x-rfa': '.rfa',
  'application/ifc': '.ifc',
  'application/vnd.sketchup.skp': '.skp',
  'application/autodesk-fbx': '.fbx',
  'application/x-3ds': '.3ds',
  // Structural / Collaboration / Point Cloud
  'application/x-rcp': '.rcp',
  'application/x-rcs': '.rcs',
  'application/x-nwd': '.nwd',
  'application/x-nwf': '.nwf',
  'application/x-pln': '.pln'
};

/**
 * Supported file extensions mapping
 * Used as fallback when browser doesn't report correct MIME type
 */
export const SUPPORTED_FILE_EXTENSIONS = {
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.webp': 'image/webp',
  '.gif': 'image/gif',
  '.tif': 'image/tiff',
  '.tiff': 'image/tiff',
  '.bmp': 'image/bmp',
  '.heif': 'image/heif',
  '.heic': 'image/heic',
  '.pdf': 'application/pdf',
  '.doc': 'application/msword',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.xls': 'application/vnd.ms-excel',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.ppt': 'application/vnd.ms-powerpoint',
  '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  '.dwg': 'application/vnd.dwg',
  '.dxf': 'application/dxf',
  '.dwt': 'application/dwt',
  '.dws': 'application/dws',
  '.dwf': 'application/dwf',
  '.dwfx': 'application/dwfx',
  '.dgn': 'application/vnd.microstation.dgn',
  '.rvt': 'application/x-rvt',
  '.rfa': 'application/x-rfa',
  '.ifc': 'application/ifc',
  '.skp': 'application/vnd.sketchup.skp',
  '.fbx': 'application/autodesk-fbx',
  '.3ds': 'application/x-3ds',
  '.rcp': 'application/x-rcp',
  '.rcs': 'application/x-rcs',
  '.nwd': 'application/x-nwd',
  '.nwf': 'application/x-nwf',
  '.pln': 'application/x-pln'
};

/**
 * Allowed folder types for file organization
 */
export const FOLDER_TYPES = {
  PROFILE_PICTURE: 'ProfilePicture',
  LICENSE_DOCUMENTS: 'LicenseDocuments',
  PROJECTS: 'Projects',
  QUOTATIONS: 'Quotations',
  OFFERS: 'Offers',
  MATERIALS: 'Materials'
};

/**
 * Maximum file size in bytes (20MB per file)
 */
export const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB in bytes

/**
 * Maximum total file size in bytes (50MB for all files together)
 */
export const MAX_TOTAL_FILE_SIZE = 50 * 1024 * 1024; // 50MB in bytes

/**
 * Get file extension from filename
 * @param {string} fileName - The file name
 * @returns {string} The file extension in lowercase
 */
function getFileExtensionFromName(fileName) {
  const lastDot = fileName.lastIndexOf('.');
  if (lastDot === -1) return '';
  return fileName.substring(lastDot).toLowerCase();
}

/**
 * Validate file type against supported content types
 * Checks both MIME type and file extension as fallback
 * @param {File} file - The file to validate
 * @returns {boolean} True if file type is supported
 */
export function validateFileType(file) {
  // First check MIME type
  if (file.type && Object.keys(SUPPORTED_CONTENT_TYPES).includes(file.type)) {
    return true;
  }
  
  // Fallback: check file extension (browsers may report empty or incorrect MIME types for some files)
  const extension = getFileExtensionFromName(file.name);
  if (extension && Object.keys(SUPPORTED_FILE_EXTENSIONS).includes(extension)) {
    return true;
  }
  
  return false;
}

/**
 * Validate file size - No individual file limit, only total limit applies
 * @param {File} file - The file to validate
 * @returns {boolean} Always returns true (individual file size check removed)
 */
export function validateFileSize(file) {
  // No individual file size limit - only total size limit (50MB) applies
  return true;
}

/**
 * Validate total file size for multiple files (max 50MB total)
 * @param {Array<File>} files - Array of files to validate
 * @returns {object} Validation result with success status and error message
 */
export function validateTotalFileSize(files) {
  if (!files || files.length === 0) {
    return {
      success: true,
      error: null
    };
  }

  const totalSize = files.reduce((sum, file) => sum + (file.size || 0), 0);
  
  if (totalSize > MAX_TOTAL_FILE_SIZE) {
    const totalSizeMB = (totalSize / (1024 * 1024)).toFixed(2);
    return {
      success: false,
      error: `Total file size (${totalSizeMB} MB) exceeds 50 MB limit. Please reduce the total size of all files.`
    };
  }

  return {
    success: true,
    error: null
  };
}

/**
 * Comprehensive file validation (type only - no individual size limit)
 * @param {File} file - The file to validate
 * @returns {object} Validation result with success status and error message
 */
export function validateFile(file) {
  if (!validateFileType(file)) {
    return {
      success: false,
      error: `File type not supported. Supported formats: Images (JPG, PNG, WebP, GIF, TIF, BMP), Documents (PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX), CAD files (DWG, DXF, DWT, DWS, DWF, DWFX), BIM files (RVT, RFA, IFC), Point Cloud files (RCP, RCS, NWD, NWF, PLN), and other 3D Models (DGN, SKP, FBX, 3DS).`
    };
  }
  
  // No individual file size check - only total size limit (50MB) applies
  
  return {
    success: true,
    error: null
  };
}

/**
 * Allowed content types for design files (Images, Documents, PDFs, CAD, BIM, Point Cloud)
 * Used for consultant file uploads
 */
export const DESIGN_FILE_CONTENT_TYPES = {
  // Images
  'image/jpeg': '.jpg',
  'image/jpg': '.jpg',
  'image/png': '.png',
  'image/webp': '.webp',
  'image/gif': '.gif',
  'image/tiff': '.tif',
  'image/bmp': '.bmp',
  'image/heif': '.heif',
  'image/heic': '.heic',
  // Documents
  'application/pdf': '.pdf',
  'application/msword': '.doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
  'application/vnd.ms-excel': '.xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'application/vnd.ms-powerpoint': '.ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
  // CAD / BIM / 3D Models
  'application/vnd.dwg': '.dwg',
  'application/acad': '.dwg',
  'application/x-acad': '.dwg',
  'application/autocad_dwg': '.dwg',
  'application/dwg': '.dwg',
  'image/vnd.dwg': '.dwg',
  'application/dxf': '.dxf',
  'application/vnd.microstation.dgn': '.dgn',
  'application/x-rvt': '.rvt',
  'application/x-rfa': '.rfa',
  'application/ifc': '.ifc',
  'application/vnd.sketchup.skp': '.skp',
  'application/autodesk-fbx': '.fbx',
  'application/x-3ds': '.3ds',
  // Point cloud / Collaboration files
  'application/x-rcp': '.rcp',
  'application/x-rcs': '.rcs',
  'application/x-nwd': '.nwd',
  'application/x-nwf': '.nwf',
  'application/x-pln': '.pln'
};

/**
 * Allowed file extensions for design files
 */
export const DESIGN_FILE_EXTENSIONS = {
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.webp': 'image/webp',
  '.gif': 'image/gif',
  '.tif': 'image/tiff',
  '.tiff': 'image/tiff',
  '.bmp': 'image/bmp',
  '.heif': 'image/heif',
  '.heic': 'image/heic',
  '.pdf': 'application/pdf',
  '.doc': 'application/msword',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.xls': 'application/vnd.ms-excel',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.ppt': 'application/vnd.ms-powerpoint',
  '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  // CAD / BIM / 3D Models
  '.dwg': 'application/vnd.dwg',
  '.dxf': 'application/dxf',
  '.dgn': 'application/vnd.microstation.dgn',
  '.rvt': 'application/x-rvt',
  '.rfa': 'application/x-rfa',
  '.ifc': 'application/ifc',
  '.skp': 'application/vnd.sketchup.skp',
  '.fbx': 'application/autodesk-fbx',
  '.3ds': 'application/x-3ds',
  // Point cloud / Collaboration files
  '.rcp': 'application/x-rcp',
  '.rcs': 'application/x-rcs',
  '.nwd': 'application/x-nwd',
  '.nwf': 'application/x-nwf',
  '.pln': 'application/x-pln'
};

/**
 * Validate design file type (Images, Documents, PDFs, CAD, BIM, Point Cloud files)
 * @param {File} file - The file to validate
 * @returns {boolean} True if file type is supported for design files
 */
export function validateDesignFileType(file) {
  // First check MIME type
  if (file.type && Object.keys(DESIGN_FILE_CONTENT_TYPES).includes(file.type)) {
    return true;
  }
  
  // Fallback: check file extension
  const extension = getFileExtensionFromName(file.name);
  if (extension && Object.keys(DESIGN_FILE_EXTENSIONS).includes(extension)) {
    return true;
  }
  
  return false;
}

/**
 * Comprehensive design file validation (type only - no individual size limit)
 * Allows Images, Documents, PDFs, CAD, BIM, and Point Cloud files
 * @param {File} file - The file to validate
 * @returns {object} Validation result with success status and error message
 */
export function validateDesignFile(file) {
  if (!validateDesignFileType(file)) {
    return {
      success: false,
      error: `File type not supported. Supported formats: Images (JPG, PNG, WebP, GIF, TIF, BMP), Documents (PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX), CAD/BIM/3D Models (DWG, DXF, DGN, RVT, IFC, SKP, FBX, 3DS), and Point Cloud/Collaboration files (RCP, RCS, NWD, NWF, PLN).`
    };
  }
  
  // No individual file size check - only total size limit (50MB) applies
  
  return {
    success: true,
    error: null
  };
}

/**
 * Get file extension from content type
 * @param {string} contentType - The MIME type of the file
 * @returns {string} The corresponding file extension
 */
export function getFileExtension(contentType) {
  return SUPPORTED_CONTENT_TYPES[contentType] || '';
}

/**
 * Get content type from file extension
 * Used when browser reports generic MIME types like application/octet-stream
 * @param {string} fileName - The file name
 * @returns {string} The correct MIME type based on file extension, or null if not found
 */
export function getContentTypeFromExtension(fileName) {
  const extension = getFileExtensionFromName(fileName);
  if (!extension) {
    return null;
  }
  return SUPPORTED_FILE_EXTENSIONS[extension] || null;
}

/**
 * Get the correct content type for a file
 * Uses file extension as fallback when browser reports generic MIME types
 * @param {File} file - The file object
 * @returns {string} The correct MIME type
 */
export function getCorrectContentType(file) {
  // If browser reports a valid supported MIME type, use it
  if (file.type && Object.keys(SUPPORTED_CONTENT_TYPES).includes(file.type)) {
    return file.type;
  }
  
  // Fallback to extension-based MIME type detection
  // This is especially important for CAD files like .dwg which browsers
  // often report as application/octet-stream
  const contentTypeFromExtension = getContentTypeFromExtension(file.name);
  if (contentTypeFromExtension) {
    return contentTypeFromExtension;
  }
  
  // If we can't determine the type, return the original (might be empty string)
  return file.type || 'application/octet-stream';
}

/**
 * Normalize filename to match expected extension for content type
 * Converts .jpeg to .jpg for image/jpeg, .tiff to .tif for image/tiff, etc.
 * @param {string} fileName - The original file name
 * @param {string} contentType - The MIME type of the file
 * @returns {string} The normalized file name with correct extension
 */
export function normalizeFileName(fileName, contentType) {
  if (!fileName || !contentType) {
    return fileName;
  }

  const expectedExtension = SUPPORTED_CONTENT_TYPES[contentType];
  if (!expectedExtension) {
    return fileName;
  }

  // Get the current extension from the filename
  const lastDot = fileName.lastIndexOf('.');
  if (lastDot === -1) {
    // No extension, add the expected one
    return `${fileName}${expectedExtension}`;
  }

  const currentExtension = fileName.substring(lastDot).toLowerCase();
  const nameWithoutExtension = fileName.substring(0, lastDot);

  // If the current extension already matches the expected one, return as-is
  if (currentExtension === expectedExtension) {
    return fileName;
  }

  // Check if the current extension maps to the same content type
  // This handles cases like .jpeg -> .jpg, .tiff -> .tif, etc.
  const currentExtensionContentType = SUPPORTED_FILE_EXTENSIONS[currentExtension];
  if (currentExtensionContentType === contentType) {
    // Replace with the expected extension
    return `${nameWithoutExtension}${expectedExtension}`;
  }

  // If extension doesn't match content type, return original filename
  // (This might indicate an issue, but we'll let the backend handle validation)
  return fileName;
}

/**
 * Request presigned URLs for file uploads
 * @param {Array} files - Array of file objects with metadata
 * @param {string} accessToken - Optional access token to use for authentication
 * @returns {Promise<Array>} Array of presigned URL responses
 */
export async function requestPresignedUrls(files, accessToken = null, retryCount = 0) {
  const MAX_RETRIES = 1; // Only retry once after token refresh
  
  try {
    const requestData = files.map(file => ({
      id: file.id,
      fileName: file.fileName,
      contentType: file.contentType,
      folderType: file.folderType,
      fileSize: file.file?.size || file.size || 0
    }));

    let response;
    
    const storedToken = localStorage.getItem('access_token');
    const effectiveToken = (accessToken && typeof accessToken === 'string') ? accessToken : storedToken;

    if (effectiveToken) {
      // Use explicit or stored token
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${effectiveToken}`
      };

      const API_BASE_URL = getNormalizedApiBaseUrl();
      const fetchResponse = await fetch(`${API_BASE_URL}/Assets/presign-urls`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(requestData)
      });

      // Handle 401 Unauthorized - refresh token and retry
      if (fetchResponse.status === 401 && retryCount < MAX_RETRIES) {
        const refreshedTokens = await refreshAccessToken();

        if (refreshedTokens?.accessToken) {
          return requestPresignedUrls(files, refreshedTokens.accessToken, retryCount + 1);
        } else {
          throw new Error('Failed to refresh access token. Please login again.');
        }
      }

      if (!fetchResponse.ok) {
        throw new Error(`HTTP ${fetchResponse.status}: ${fetchResponse.statusText}`);
      }

      response = await fetchResponse.json();
    } else {
      // No token available — unauthenticated request (e.g. registration / license-update flow)
      response = await publicApiService.post('/Assets/presign-urls', requestData);
    }

    // Handle the response structure: { success: true, result: [...] }
    if (response.success && Array.isArray(response.result)) {
      return response.result; // Return the result array
    } else {
      throw new Error('Invalid response structure from presigned URLs API');
    }
  } catch (error) {
    throw error;
  }
}

/**
 * Upload file to S3 using presigned URL
 * @param {string} presignedUrl - The presigned URL for upload
 * @param {File} file - The file to upload
 * @param {string} contentType - The content type of the file
 * @returns {Promise<Object>} Upload result with etag and status
 */
export async function uploadToPresignedUrl(presignedUrl, file, contentType) {
  try {


    const response = await fetch(presignedUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': contentType
      },
      body: file
    });



    if (!response.ok) {
      const errorText = await response.text();
      console.error('S3 Upload Failed - Status:', response.status);
      console.error('S3 Upload Failed - Response:', errorText);
      throw new Error(`Upload failed: ${response.status} - ${errorText}`);
    }

    const etag = response.headers.get('ETag');


    return { etag, status: response.status };
  } catch (error) {
    console.error('❌ Failed to upload file to S3:', error);
    console.error('Error details:', {
      message: error.message,
      stack: error.stack,
      presignedUrl: presignedUrl,
      fileName: file.name,
      contentType: contentType
    });
    throw error;
  }
}

/**
 * Upload multiple files using presigned URLs
 * @param {Array} files - Array of File objects
 * @param {string} folderType - The folder type for organization
 * @param {Function} onProgress - Optional progress callback
 * @param {string} accessToken - Optional access token to use for authentication
 * @returns {Promise<Array>} Array of upload results with file keys
 */
export async function uploadFiles(files, folderType, onProgress = null, accessToken = null) {
  try {
    // Validate all files first
    const invalidFiles = files.filter(file => !validateFileType(file));
    if (invalidFiles.length > 0) {
      throw new Error(`Unsupported file types: ${invalidFiles.map(f => f.name).join(', ')}`);
    }

    // Prepare file metadata
    const fileMetadata = files.map(file => {
      const correctContentType = getCorrectContentType(file);
      return {
        id: generateGUID(),
        fileName: normalizeFileName(file.name, correctContentType),
        contentType: correctContentType,
        folderType: folderType,
        file: file
      };
    });

    // Request presigned URLs
    const presignedResponses = await requestPresignedUrls(fileMetadata, accessToken);

    // Upload files to S3
    const uploadResults = [];



    for (let i = 0; i < fileMetadata.length; i++) {
      const fileMeta = fileMetadata[i];

      // Find the matching presigned response by fileName
      const presignedResponse = presignedResponses.find(item =>
        item.fileName === fileMeta.fileName
      );

      if (!presignedResponse) {
        console.error(`No presigned response found for file: ${fileMeta.fileName}`);
        uploadResults.push({
          id: fileMeta.id,
          fileName: fileMeta.fileName,
          error: 'No presigned response found',
          success: false
        });
        continue;
      }

      console.log('🔗 Presigned URL Response:', {
        requestUrl: presignedResponse.requestUrl,
        key: presignedResponse.key,
        fileName: presignedResponse.fileName,
        contentType: fileMeta.contentType,
      });

      try {
        const uploadResult = await uploadToPresignedUrl(
          presignedResponse.requestUrl,
          fileMeta.file,
          fileMeta.contentType
        );

        uploadResults.push({
          id: fileMeta.id,
          fileName: fileMeta.fileName,
          key: presignedResponse.key,
          url: presignedResponse.fileUrl || presignedResponse.url || presignedResponse.key,
          fileUrl: presignedResponse.fileUrl || presignedResponse.url || presignedResponse.key,
          etag: uploadResult.etag,
          status: uploadResult.status,
          success: true
        });

        // Call progress callback if provided
        if (onProgress) {
          onProgress({
            completed: i + 1,
            total: files.length,
            currentFile: fileMeta.fileName,
            percentage: Math.round(((i + 1) / files.length) * 100)
          });
        }
      } catch (error) {
        console.error(`Failed to upload file ${fileMeta.fileName}:`, error);
        uploadResults.push({
          id: fileMeta.id,
          fileName: fileMeta.fileName,
          error: error.message,
          success: false
        });
      }
    }

    return uploadResults;
  } catch (error) {
    console.error('Failed to upload files:', error);
    throw error;
  }
}

/**
 * Upload a single file
 * @param {File} file - The file to upload
 * @param {string} folderType - The folder type for organization
 * @param {Function} onProgress - Optional progress callback
 * @returns {Promise<Object>} Upload result with file key
 */
export async function uploadSingleFile(file, folderType, onProgress = null, accessToken = null) {
  try {
    const results = await uploadFiles([file], folderType, onProgress, accessToken);
    return results[0];
  } catch (error) {
    console.error('Failed to upload single file:', error);
    throw error;
  }
}

/**
 * Get file URL from S3 key (for display purposes)
 * @param {string} key - The S3 key
 * @returns {string} The file URL
 */
export function getFileUrl(key) {
  // This would typically construct the URL from your S3 bucket configuration
  // For now, returning the key as-is, but you might want to prepend your S3 base URL
  return key;
}

/**
 * Upload multiple files with a single presigned URL request
 * This is more efficient than making separate requests for each file type
 * @param {Array} allFiles - Array of file objects with metadata
 * @param {Function} onProgress - Optional progress callback
 * @param {string} accessToken - Optional access token to use for authentication. Pass null for public uploads (uses publicApiService)
 * @returns {Promise<Object>} Upload results organized by category
 */
export async function uploadFilesBatch(allFiles, onProgress = null, accessToken = null) {
  try {
    // Validate all files first
    const invalidFiles = allFiles.filter(fileData => !validateFileType(fileData.file));
    if (invalidFiles.length > 0) {
      throw new Error(`Unsupported file types: ${invalidFiles.map(f => f.fileName).join(', ')}`);
    }

    // Normalize filenames and ensure correct content types
    const normalizedFiles = allFiles.map(fileData => {
      const file = fileData.file;
      const correctContentType = file ? getCorrectContentType(file) : (fileData.contentType || 'application/octet-stream');
      const fileName = fileData.fileName || file?.name || '';
      return {
        ...fileData,
        fileName: normalizeFileName(fileName, correctContentType),
        contentType: correctContentType
      };
    });

    // Request presigned URLs for all files in one call
    const presignedResponses = await requestPresignedUrls(normalizedFiles, accessToken);

    // Upload all files to S3
    const uploadResults = {};
    const portfolioUrls = [];
    
    for (let i = 0; i < normalizedFiles.length; i++) {
      const fileData = normalizedFiles[i];
      // Match by id instead of fileName to handle cases where same file is used for different categories
      const presignedResponse = presignedResponses.find(resp => resp.id === fileData.id);
      
      if (!presignedResponse) {
        console.error(`No presigned response found for file: ${fileData.fileName} (id: ${fileData.id})`);
        continue;
      }

      try {
        // Call progress callback if provided
        if (onProgress) {
          onProgress({
            completed: i,
            total: normalizedFiles.length,
            currentFile: fileData.fileName,
            percentage: Math.round((i / normalizedFiles.length) * 100),
            category: fileData.category
          });
        }

        const uploadResult = await uploadToPresignedUrl(
          presignedResponse.requestUrl,
          fileData.file,
          fileData.contentType
        );

        // Organize results by category
        const category = fileData.category;
        if (category === 'portfolio') {
          portfolioUrls.push(presignedResponse.key);
        } else {
          uploadResults[`${category}Url`] = presignedResponse.key;
        }

      } catch (error) {
        console.error(`❌ Failed to upload ${fileData.category} (${fileData.fileName}):`, error);
        // Continue with other files even if one fails
      }
    }

    // Set portfolio URLs array
    if (portfolioUrls.length > 0) {
      uploadResults.portfolioUrl = portfolioUrls;
    }

    // Final progress callback
    if (onProgress) {
      onProgress({
        completed: normalizedFiles.length,
        total: normalizedFiles.length,
        currentFile: null,
        percentage: 100,
        category: 'completed'
      });
    }

    return uploadResults;
  } catch (error) {
    console.error('❌ Batch upload failed:', error);
    throw error;
  }
}

/**
 * File upload service with all common functionality
 */
export const fileUploadService = {
  validateFileType,
  getFileExtension,
  getContentTypeFromExtension,
  getCorrectContentType,
  normalizeFileName,
  requestPresignedUrls,
  uploadToPresignedUrl,
  uploadFiles,
  uploadSingleFile,
  uploadFilesBatch,
  getFileUrl,
  SUPPORTED_CONTENT_TYPES,
  FOLDER_TYPES
};

/**
 * Test function to manually test S3 upload with a presigned URL
 * @param {string} presignedUrl - The presigned URL from API
 * @param {File} file - The file to upload
 * @param {string} contentType - The content type
 */
export async function testS3Upload(presignedUrl, file, contentType) {


  try {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': contentType
      },
      body: file
    });



    if (response.ok) {

      return { success: true, etag: response.headers.get('ETag') };
    } else {
      const errorText = await response.text();
      console.error('❌ Test upload failed:', errorText);
      return { success: false, error: errorText };
    }
  } catch (error) {
    console.error('❌ Test upload error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Upload profile picture and banner images using presigned URLs
 * @param {Object} images - Object containing profile picture and banner
 * @param {File} images.profilePicture - Profile picture file
 * @param {File} images.banner - Banner image file
 * @returns {Promise<Object>} Object with uploaded URLs
 */
export async function uploadProfileImages(images, accessToken = null) {
  try {
    const { profilePicture, banner } = images;
    const filesToUpload = [];
    const uploadResults = {};

    // Prepare profile picture for upload
    if (profilePicture) {
      const profilePictureContentType = getCorrectContentType(profilePicture);
      filesToUpload.push({
        id: generateGUID(),
        fileName: normalizeFileName(profilePicture.name, profilePictureContentType),
        contentType: profilePictureContentType,
        folderType: FOLDER_TYPES.PROFILE_PICTURE,
        file: profilePicture,
        category: 'profilePicture'
      });
    }

    // Prepare banner for upload
    if (banner) {
      const bannerContentType = getCorrectContentType(banner);
      filesToUpload.push({
        id: generateGUID(),
        fileName: normalizeFileName(banner.name, bannerContentType),
        contentType: bannerContentType,
        folderType: FOLDER_TYPES.PROFILE_PICTURE, // Using same folder as per requirements
        file: banner,
        category: 'banner'
      });
    }

    if (filesToUpload.length === 0) {
      return { profilePictureURL: null, bannerURL: null };
    }

    // Request presigned URLs with token
    const presignedResponses = await requestPresignedUrls(filesToUpload, accessToken);

    // Upload files to S3
    for (let i = 0; i < filesToUpload.length; i++) {
      const file = filesToUpload[i];
      const presignedResponse = presignedResponses[i];

      if (presignedResponse && presignedResponse.key) {
        await uploadToPresignedUrl(presignedResponse.requestUrl, file.file, file.contentType);

        // Map the uploaded URL based on category using the key from API response
        if (file.category === 'profilePicture') {
          uploadResults.profilePictureURL = presignedResponse.key;
        } else if (file.category === 'banner') {
          uploadResults.bannerURL = presignedResponse.key;
        }
      }
    }
    return uploadResults;
  } catch (error) {
    console.error('❌ Failed to upload profile images:', error);
    throw error;
  }
}

export default fileUploadService;
